import { useState, useEffect, useRef, useCallback } from "react"
import type { MapRef } from "react-map-gl/mapbox"
import { Loader2, Search, X } from "lucide-react"
import { CommuneSelector } from "./commune-selector"
import { AddressResultsDropdown } from "./address-results-dropdown"

interface AddressResult {
  center: [number, number]
  label: string
}

export interface AddressFeature {
  geometry: { type: string; coordinates: [number, number] }
  properties: {
    name: string
    postcode?: string
    city?: string
    type: string
    id: string
    label: string
  }
}

interface AddressSearchBarProps {
  onSelectCommune: (code: string) => Promise<void>
  communes?: { code_com: string; code_dep: string; nom: string; latitude: number; longitude: number }[]
  selectedCommune?: { code?: string; codeDepartement?: string; libelle?: string } | null
  addressSearch: string
  setAddressSearch: (value: string) => void
  onAddressSelect?: (result: AddressResult) => void
  setLoading?: (key: string, active: boolean) => void
  mapRef: React.RefObject<MapRef | null>
  onHoverResult?: (lngLat: [number, number]) => void
  onLeaveResult?: () => void
  onClearAddress?: () => void
  isCommuneCovered?: boolean
}

export const AddressSearchBar = ({
  onSelectCommune,
  communes,
  selectedCommune,
  addressSearch,
  setAddressSearch,
  onAddressSelect,
  mapRef,
  onHoverResult,
  onLeaveResult,
  onClearAddress,
  isCommuneCovered = true,
}: AddressSearchBarProps) => {
  const [addressResults, setAddressResults] = useState<AddressFeature[]>([])
  const [hasFetched, setHasFetched] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const searchAddress = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (query.length < 3) {
        setAddressResults([])
        setHasFetched(false)
        return
      }
      setIsSearching(true)
      debounceRef.current = setTimeout(async () => {
        try {
          let url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
          const center = mapRef.current?.getCenter()
          if (center) {
            url += `&lat=${center.lat}&lon=${center.lng}`
          }
          const res = await fetch(`/api/explore/geo_proxy?url=${encodeURIComponent(url)}`)
          const data = await res.json()
          setAddressResults(data.features || [])
          setHighlightedIndex(-1)
        } catch {
          setAddressResults([])
          setHighlightedIndex(-1)
        }
        setHasFetched(true)
        setIsSearching(false)
      }, 300)
    },
    [mapRef],
  )

  const selectResult = useCallback(
    (feature: AddressFeature) => {
      const [lng, lat] = feature.geometry.coordinates
      setAddressSearch(feature.properties.label)
      setAddressResults([])
      setHasFetched(false)
      onAddressSelect?.({
        center: [lng, lat],
        label: feature.properties.label,
      })
    },
    [setAddressSearch, onAddressSelect],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isFocused || !hasFetched || addressResults.length === 0) return
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlightedIndex((i) => Math.min(i + 1, addressResults.length - 1))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlightedIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === "Enter") {
        e.preventDefault()
        const index = highlightedIndex === -1 ? 0 : highlightedIndex
        selectResult(addressResults[index])
      }
    },
    [isFocused, hasFetched, addressResults, highlightedIndex, selectResult],
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    }
  }, [])

  const showDropdown = isFocused && hasFetched && addressSearch.length >= 3

  return (
    <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30" style={{ width: 360 }}>
      <div className="bg-white/95 backdrop-blur-sm rounded-xl map-panel-shadow anim-fade-in">
        {/* Commune row */}
        <CommuneSelector
          onSelectCommune={onSelectCommune}
          communes={communes}
          selectedCommune={selectedCommune}
          isCommuneCovered={isCommuneCovered}
        />

        {/* Divider */}
        <div className="mx-3 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

        {/* Address row */}
        <div data-tour-step="address-search" className="relative p-2">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <Search className="w-3.5 h-3.5" />
            </span>
            <input
              type="text"
              data-address-search
              value={addressSearch}
              onChange={(e) => {
                setAddressSearch(e.target.value)
                searchAddress(e.target.value)
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
                setIsFocused(true)
              }}
              onBlur={() => {
                blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 150)
              }}
              placeholder="Rechercher une adresse..."
              className={`w-full focus:outline-none text-[0.9375rem] h-8 pl-[30px] pr-7 rounded-md border-none transition-colors duration-150 text-gray-800 ${
                isFocused ? "bg-gray-50" : "bg-transparent"
              }`}
            />
            {isSearching ? (
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center justify-center">
                <Loader2 className="w-3.5 h-3.5 text-blue-800 animate-spin" />
              </span>
            ) : (
              addressSearch.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setAddressSearch("")
                    setAddressResults([])
                    setHasFetched(false)
                    onClearAddress?.()
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-md transition-colors w-5 h-5 text-gray-400"
                >
                  <X className="w-3 h-3" />
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* Address results dropdown */}
      {showDropdown && (
        <AddressResultsDropdown
          results={addressResults}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          selectResult={selectResult}
          mapRef={mapRef}
          onHoverResult={onHoverResult}
          onLeaveResult={onLeaveResult}
        />
      )}
    </div>
  )
}
